import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { Briefcase, Plus, Pencil, Trash2, ChevronRight, ListChecks } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const STATI = ['Preventivo', 'Attiva', 'In attesa cliente', 'In attesa ente', 'Sospesa', 'Conclusa', 'Archiviata'];
const PRIORITA = ['Alta', 'Media', 'Bassa'];
const TIPI_PRATICA = ['CILA', 'SCIA', 'PdC', 'Catasto', 'Soprintendenza', 'Genio Civile', 'Legge 10', 'APE', 'Direzione Lavori', 'Sicurezza', 'Perizia', 'Altro'];
const STATO_COLORS = { Preventivo: 'bg-blue-100 text-blue-800', Attiva: 'bg-emerald-100 text-emerald-800', 'In attesa cliente': 'bg-yellow-100 text-yellow-800', 'In attesa ente': 'bg-amber-100 text-amber-800', Sospesa: 'bg-orange-100 text-orange-800', Conclusa: 'bg-gray-100 text-gray-800', Archiviata: 'bg-gray-200 text-gray-600' };
const PRIO_COLORS = { Alta: 'bg-red-100 text-red-700', Media: 'bg-amber-100 text-amber-700', Bassa: 'bg-gray-100 text-gray-700' };

const fmtEur = (v) => `€ ${(v || 0).toLocaleString('it-IT')}`;
const emptyIter = { urbanistica: 0, catasto: 0, soprintendenza: 0, genio_civile: 0, energetica: 0, sicurezza: 0 };
const empty = {
  cliente_id: '', oggetto: '', comune: '', tipo_pratica: 'SCIA', stato: 'Preventivo', priorita: 'Media',
  importo_lavori: 0, parcella_prevista: 0, ore_stimate: 0,
  iter_autorizzativo: { ...emptyIter }, note: '', data_inizio: '', data_fine_prevista: ''
};

export default function StudioCommessePage() {
  const [items, setItems] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [statoFilter, setStatoFilter] = useState('__all__');
  const [expanded, setExpanded] = useState(null);
  // SAL & Servizi dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCommessa, setDetailCommessa] = useState(null);
  const [salList, setSalList] = useState([]);
  const [serviziList, setServiziList] = useState([]);
  const [salForm, setSalForm] = useState({ numero: 1, data: '', descrizione: '', percentuale: 0, importo: 0, note: '' });
  const [servForm, setServForm] = useState({ tipo: 'SCIA', descrizione: '', stato: 'Da iniziare', data_inizio: '', data_completamento: '', numero_protocollo: '', importo: 0, note: '' });
  const [editSalId, setEditSalId] = useState(null);
  const [editServId, setEditServId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [c, cl] = await Promise.all([studioApi.getCommesse(), studioApi.getClienti()]);
      setItems(c.data); setClienti(cl.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      ...empty, ...c,
      iter_autorizzativo: { ...emptyIter, ...(c.iter_autorizzativo || {}) },
    });
    setEditingId(c.id); setOpen(true);
  };
  const save = async () => {
    if (!form.cliente_id || !form.oggetto) { toast.error('Cliente e oggetto obbligatori'); return; }
    const payload = {
      ...form,
      importo_lavori: parseFloat(form.importo_lavori) || 0,
      parcella_prevista: parseFloat(form.parcella_prevista) || 0,
      ore_stimate: parseFloat(form.ore_stimate) || 0,
    };
    try {
      if (editingId) { await studioApi.updateCommessa(editingId, payload); toast.success('Aggiornata'); }
      else { await studioApi.createCommessa(payload); toast.success('Creata'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare commessa "${c.codice}"?\nVerranno cancellati anche pagamenti, scadenze e SAL collegati.`)) return;
    try { await studioApi.deleteCommessa(c.id); toast.success('Eliminata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  // --- SAL & Servizi ---
  const openDetail = async (c) => {
    setDetailCommessa(c);
    setDetailOpen(true);
    setEditSalId(null); setEditServId(null);
    setSalForm({ numero: (salList.length || 0) + 1, data: new Date().toISOString().slice(0, 10), descrizione: '', percentuale: 0, importo: 0, note: '' });
    setServForm({ tipo: 'SCIA', descrizione: '', stato: 'Da iniziare', data_inizio: '', data_completamento: '', numero_protocollo: '', importo: 0, note: '' });
    await reloadDetail(c.id);
  };
  const reloadDetail = async (commessaId) => {
    try {
      const [s, sv] = await Promise.all([studioApi.getSal(commessaId), studioApi.getServizi(commessaId)]);
      setSalList(s.data); setServiziList(sv.data);
    } catch { toast.error('Errore caricamento dettaglio'); }
  };
  const saveSal = async () => {
    if (!salForm.descrizione) { toast.error('Descrizione SAL obbligatoria'); return; }
    const payload = {
      commessa_id: detailCommessa.id,
      numero: parseInt(salForm.numero) || 1,
      data: salForm.data || null,
      descrizione: salForm.descrizione,
      percentuale: parseFloat(salForm.percentuale) || 0,
      importo: parseFloat(salForm.importo) || 0,
      note: salForm.note || '',
    };
    try {
      if (editSalId) await studioApi.updateSal(editSalId, payload);
      else await studioApi.createSal(payload);
      toast.success(editSalId ? 'SAL aggiornato' : 'SAL aggiunto');
      setEditSalId(null);
      setSalForm({ numero: (salList.length || 0) + 2, data: new Date().toISOString().slice(0, 10), descrizione: '', percentuale: 0, importo: 0, note: '' });
      reloadDetail(detailCommessa.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const editSal = (s) => {
    setEditSalId(s.id);
    setSalForm({ numero: s.numero || 1, data: s.data || '', descrizione: s.descrizione || '', percentuale: s.percentuale || 0, importo: s.importo || 0, note: s.note || '' });
  };
  const removeSal = async (s) => {
    if (!window.confirm(`Eliminare SAL n.${s.numero}?`)) return;
    try { await studioApi.deleteSal(s.id); toast.success('SAL eliminato'); reloadDetail(detailCommessa.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const saveServizio = async () => {
    if (!servForm.tipo) { toast.error('Tipo servizio obbligatorio'); return; }
    const payload = {
      commessa_id: detailCommessa.id,
      tipo: servForm.tipo,
      descrizione: servForm.descrizione || '',
      stato: servForm.stato,
      data_inizio: servForm.data_inizio || null,
      data_completamento: servForm.data_completamento || null,
      numero_protocollo: servForm.numero_protocollo || '',
      importo: parseFloat(servForm.importo) || 0,
      note: servForm.note || '',
    };
    try {
      if (editServId) await studioApi.updateServizio(editServId, payload);
      else await studioApi.createServizio(payload);
      toast.success(editServId ? 'Servizio aggiornato' : 'Servizio aggiunto');
      setEditServId(null);
      setServForm({ tipo: 'SCIA', descrizione: '', stato: 'Da iniziare', data_inizio: '', data_completamento: '', numero_protocollo: '', importo: 0, note: '' });
      reloadDetail(detailCommessa.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const editServizio = (s) => {
    setEditServId(s.id);
    setServForm({ tipo: s.tipo || 'SCIA', descrizione: s.descrizione || '', stato: s.stato || 'Da iniziare', data_inizio: s.data_inizio || '', data_completamento: s.data_completamento || '', numero_protocollo: s.numero_protocollo || '', importo: s.importo || 0, note: s.note || '' });
  };
  const removeServizio = async (s) => {
    if (!window.confirm(`Eliminare servizio "${s.tipo}"?`)) return;
    try { await studioApi.deleteServizio(s.id); toast.success('Servizio eliminato'); reloadDetail(detailCommessa.id); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const filtered = items.filter(c => statoFilter === '__all__' || c.stato === statoFilter);

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="studio-commesse-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><Briefcase className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Commesse</h1><Badge>{items.length}</Badge></div>
          <p className="text-sm text-gray-600 mt-1">Gestione pratiche e progetti dello studio</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={statoFilter} onValueChange={setStatoFilter}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutti gli stati</SelectItem>
              {STATI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="new-commessa-btn"><Plus className="w-4 h-4 mr-1" /> Nuova commessa</Button>
        </div>
      </motion.div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="text-left p-2.5">Codice</th>
                <th className="text-left p-2.5">Cliente</th>
                <th className="text-left p-2.5">Oggetto</th>
                <th className="text-left p-2.5">Pratica</th>
                <th className="text-left p-2.5">Stato</th>
                <th className="text-right p-2.5">Parcella</th>
                <th className="text-right p-2.5">Pagato</th>
                <th className="text-center p-2.5">Avanz.</th>
                <th className="text-center p-2.5">Iter</th>
                <th className="text-center p-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t hover:bg-gray-50" data-testid={`commessa-row-${c.id}`}>
                  <td className="p-2.5 font-mono text-xs text-indigo-700">{c.codice}</td>
                  <td className="p-2.5">{c.cliente_nome || '-'}</td>
                  <td className="p-2.5 max-w-xs truncate" title={c.oggetto}>{c.oggetto}</td>
                  <td className="p-2.5"><Badge variant="outline" className="text-xs">{c.tipo_pratica}</Badge></td>
                  <td className="p-2.5"><Badge className={`text-xs ${STATO_COLORS[c.stato] || ''}`}>{c.stato}</Badge> {c.priorita && <Badge className={`text-xs ml-1 ${PRIO_COLORS[c.priorita] || ''}`}>{c.priorita}</Badge>}</td>
                  <td className="p-2.5 text-right font-mono">{fmtEur(c.parcella_prevista)}</td>
                  <td className="p-2.5 text-right font-mono text-emerald-700">{fmtEur(c.totale_pagato)}</td>
                  <td className="p-2.5 text-center"><span className="font-semibold">{Math.round((c.avanzamento || 0) * 100)}%</span></td>
                  <td className="p-2.5 text-center text-xs">{Math.round((c.iter_avanzamento || 0) * 100)}%</td>
                  <td className="p-2.5 text-right">
                    <Button size="icon" variant="ghost" onClick={() => openDetail(c)} title="SAL & Servizi" data-testid={`detail-commessa-${c.id}`}><ListChecks className="w-3.5 h-3.5 text-indigo-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(c)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="10" className="text-center text-gray-500 italic py-10">Nessuna commessa.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica commessa' : 'Nuova commessa'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Cliente *</Label>
              <Select value={form.cliente_id || '__none__'} onValueChange={(v) => setForm({ ...form, cliente_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="commessa-cliente"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- nessuno --</SelectItem>
                  {clienti.map(cl => <SelectItem key={cl.id} value={cl.id}>{cl.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Comune</Label><Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Oggetto *</Label><Input value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} data-testid="commessa-oggetto" /></div>
            <div><Label>Tipo pratica</Label>
              <Select value={form.tipo_pratica} onValueChange={(v) => setForm({ ...form, tipo_pratica: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI_PRATICA.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATI.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Priorità</Label>
              <Select value={form.priorita} onValueChange={(v) => setForm({ ...form, priorita: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITA.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Importo lavori (€)</Label><Input type="number" value={form.importo_lavori} onChange={(e) => setForm({ ...form, importo_lavori: e.target.value })} /></div>
            <div><Label>Parcella prevista (€)</Label><Input type="number" value={form.parcella_prevista} onChange={(e) => setForm({ ...form, parcella_prevista: e.target.value })} /></div>
            <div><Label>Ore stimate</Label><Input type="number" value={form.ore_stimate} onChange={(e) => setForm({ ...form, ore_stimate: e.target.value })} /></div>
            <div><Label>Data inizio</Label><Input type="date" value={form.data_inizio || ''} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} /></div>
            <div><Label>Data fine prevista</Label><Input type="date" value={form.data_fine_prevista || ''} onChange={(e) => setForm({ ...form, data_fine_prevista: e.target.value })} /></div>
            <div className="md:col-span-2 border-t pt-3 mt-2">
              <Label className="text-base font-semibold">Iter autorizzativo (0-100% per step)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {[
                  ['urbanistica', 'Urbanistica'],
                  ['catasto', 'Catasto'],
                  ['soprintendenza', 'Soprintendenza'],
                  ['genio_civile', 'Genio Civile'],
                  ['energetica', 'Energetica/L10'],
                  ['sicurezza', 'Sicurezza'],
                ].map(([k, lbl]) => (
                  <div key={k}>
                    <Label className="text-xs">{lbl}</Label>
                    <Input type="number" min="0" max="1" step="0.1" value={form.iter_autorizzativo?.[k] ?? 0}
                      onChange={(e) => setForm({ ...form, iter_autorizzativo: { ...(form.iter_autorizzativo || {}), [k]: parseFloat(e.target.value) || 0 } })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-commessa-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SAL & Servizi dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {detailCommessa ? `${detailCommessa.codice} — ${detailCommessa.oggetto}` : 'Dettaglio commessa'}
            </DialogTitle>
          </DialogHeader>
          {detailCommessa && (
            <Tabs defaultValue="sal" className="w-full">
              <TabsList>
                <TabsTrigger value="sal" data-testid="tab-sal">SAL (Stato Avanzamento Lavori)</TabsTrigger>
                <TabsTrigger value="servizi" data-testid="tab-servizi">Servizi Erogati</TabsTrigger>
              </TabsList>

              {/* SAL TAB */}
              <TabsContent value="sal" className="space-y-4">
                <div className="bg-gray-50 border rounded-lg p-3">
                  <h3 className="font-semibold text-sm mb-2">{editSalId ? 'Modifica SAL' : 'Nuovo SAL'}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div><Label className="text-xs">Numero</Label><Input type="number" min="1" value={salForm.numero} onChange={(e) => setSalForm({ ...salForm, numero: e.target.value })} /></div>
                    <div><Label className="text-xs">Data</Label><Input type="date" value={salForm.data} onChange={(e) => setSalForm({ ...salForm, data: e.target.value })} /></div>
                    <div><Label className="text-xs">% completamento</Label><Input type="number" min="0" max="100" step="1" value={salForm.percentuale} onChange={(e) => setSalForm({ ...salForm, percentuale: e.target.value })} /></div>
                    <div><Label className="text-xs">Importo (€)</Label><Input type="number" value={salForm.importo} onChange={(e) => setSalForm({ ...salForm, importo: e.target.value })} /></div>
                    <div className="md:col-span-4"><Label className="text-xs">Descrizione</Label><Input value={salForm.descrizione} onChange={(e) => setSalForm({ ...salForm, descrizione: e.target.value })} data-testid="sal-descrizione" /></div>
                    <div className="md:col-span-4"><Label className="text-xs">Note</Label><Textarea rows={2} value={salForm.note} onChange={(e) => setSalForm({ ...salForm, note: e.target.value })} /></div>
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    {editSalId && <Button size="sm" variant="outline" onClick={() => { setEditSalId(null); setSalForm({ numero: (salList.length || 0) + 1, data: new Date().toISOString().slice(0, 10), descrizione: '', percentuale: 0, importo: 0, note: '' }); }}>Annulla</Button>}
                    <Button size="sm" onClick={saveSal} data-testid="save-sal-btn"><Plus className="w-3.5 h-3.5 mr-1" /> {editSalId ? 'Aggiorna' : 'Aggiungi'} SAL</Button>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="text-left p-2">#</th>
                        <th className="text-left p-2">Data</th>
                        <th className="text-left p-2">Descrizione</th>
                        <th className="text-right p-2">%</th>
                        <th className="text-right p-2">Importo</th>
                        <th className="text-right p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {salList.map((s) => (
                        <tr key={s.id} className="border-t" data-testid={`sal-row-${s.id}`}>
                          <td className="p-2 font-mono">{s.numero}</td>
                          <td className="p-2">{s.data || '-'}</td>
                          <td className="p-2">{s.descrizione}</td>
                          <td className="p-2 text-right">{s.percentuale}%</td>
                          <td className="p-2 text-right font-mono">€ {(s.importo || 0).toLocaleString('it-IT')}</td>
                          <td className="p-2 text-right">
                            <Button size="icon" variant="ghost" onClick={() => editSal(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => removeSal(s)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                          </td>
                        </tr>
                      ))}
                      {salList.length === 0 && <tr><td colSpan="6" className="text-center text-gray-500 italic py-6">Nessun SAL registrato.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* SERVIZI TAB */}
              <TabsContent value="servizi" className="space-y-4">
                <div className="bg-gray-50 border rounded-lg p-3">
                  <h3 className="font-semibold text-sm mb-2">{editServId ? 'Modifica servizio' : 'Nuovo servizio erogato'}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">Tipo</Label>
                      <Select value={servForm.tipo} onValueChange={(v) => setServForm({ ...servForm, tipo: v })}>
                        <SelectTrigger data-testid="servizio-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['SCIA', 'PdC', 'CILA', 'Catasto', 'APE', 'Direzione Lavori', 'Sicurezza', 'Soprintendenza', 'Genio Civile', 'Legge 10', 'Perizia', 'Altro'].map(t =>
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Stato</Label>
                      <Select value={servForm.stato} onValueChange={(v) => setServForm({ ...servForm, stato: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['Da iniziare', 'In corso', 'Inviato', 'Approvato', 'Concluso'].map(s =>
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">N° protocollo</Label><Input value={servForm.numero_protocollo} onChange={(e) => setServForm({ ...servForm, numero_protocollo: e.target.value })} /></div>
                    <div><Label className="text-xs">Importo (€)</Label><Input type="number" value={servForm.importo} onChange={(e) => setServForm({ ...servForm, importo: e.target.value })} /></div>
                    <div><Label className="text-xs">Data inizio</Label><Input type="date" value={servForm.data_inizio} onChange={(e) => setServForm({ ...servForm, data_inizio: e.target.value })} /></div>
                    <div><Label className="text-xs">Data completamento</Label><Input type="date" value={servForm.data_completamento} onChange={(e) => setServForm({ ...servForm, data_completamento: e.target.value })} /></div>
                    <div className="md:col-span-4"><Label className="text-xs">Descrizione</Label><Input value={servForm.descrizione} onChange={(e) => setServForm({ ...servForm, descrizione: e.target.value })} data-testid="servizio-descrizione" /></div>
                    <div className="md:col-span-4"><Label className="text-xs">Note</Label><Textarea rows={2} value={servForm.note} onChange={(e) => setServForm({ ...servForm, note: e.target.value })} /></div>
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    {editServId && <Button size="sm" variant="outline" onClick={() => { setEditServId(null); setServForm({ tipo: 'SCIA', descrizione: '', stato: 'Da iniziare', data_inizio: '', data_completamento: '', numero_protocollo: '', importo: 0, note: '' }); }}>Annulla</Button>}
                    <Button size="sm" onClick={saveServizio} data-testid="save-servizio-btn"><Plus className="w-3.5 h-3.5 mr-1" /> {editServId ? 'Aggiorna' : 'Aggiungi'} servizio</Button>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                      <tr>
                        <th className="text-left p-2">Tipo</th>
                        <th className="text-left p-2">Stato</th>
                        <th className="text-left p-2">Descrizione</th>
                        <th className="text-left p-2">Protocollo</th>
                        <th className="text-right p-2">Importo</th>
                        <th className="text-right p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviziList.map((s) => (
                        <tr key={s.id} className="border-t" data-testid={`servizio-row-${s.id}`}>
                          <td className="p-2"><Badge variant="outline" className="text-xs">{s.tipo}</Badge></td>
                          <td className="p-2"><Badge className="text-xs">{s.stato}</Badge></td>
                          <td className="p-2 max-w-xs truncate" title={s.descrizione}>{s.descrizione || '-'}</td>
                          <td className="p-2 font-mono text-xs">{s.numero_protocollo || '-'}</td>
                          <td className="p-2 text-right font-mono">€ {(s.importo || 0).toLocaleString('it-IT')}</td>
                          <td className="p-2 text-right">
                            <Button size="icon" variant="ghost" onClick={() => editServizio(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => removeServizio(s)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                          </td>
                        </tr>
                      ))}
                      {serviziList.length === 0 && <tr><td colSpan="6" className="text-center text-gray-500 italic py-6">Nessun servizio registrato.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
